import type { KeyboardEvent as ReactKeyboardEvent } from "react";
// eslint-disable-next-line no-restricted-imports
import { ContextMenu } from "@base-ui/react/context-menu";
import { useCloseOnExclusiveOverlay } from "@design-system/overlay/lightDismiss";
import { isFromTextField } from "@design-system/overlay/textFields";
import { MenuPopup } from "./MenuPopup";
import type { MenuContextProps } from "./Menu.types";

const isContextMenuShortcut = ({ key, shiftKey }: ReactKeyboardEvent) =>
    key === "ContextMenu" || (key === "F10" && shiftKey);

/**
 * Fires the `contextmenu` Base UI opens from, positioned at the trigger's
 * bottom-left corner: a keyboard-opened menu drops from the trigger instead of
 * the pointer or the viewport's top-left.
 */
const openAtTrigger = (trigger: HTMLElement) => {
    const { left, bottom } = trigger.getBoundingClientRect();
    trigger.dispatchEvent(
        new MouseEvent("contextmenu", {
            bubbles: true,
            cancelable: true,
            clientX: left,
            clientY: bottom,
        }),
    );
};

/**
 * A right-click menu on a card or row-like surface: `trigger` opens Menu's
 * popup at the pointer on right-click or long-press, and at the trigger on
 * Shift+F10 or the Menu key. Clicks and other keys reach the trigger as usual.
 * A text field inside it, such as an inline rename, keeps the browser's menu.
 */
export const MenuContext = ({
    trigger,
    children,
    ...rootProps
}: MenuContextProps) => {
    const actionsRef = useCloseOnExclusiveOverlay();

    return (
        <ContextMenu.Root {...rootProps} actionsRef={actionsRef}>
            <ContextMenu.Trigger
                render={trigger}
                onKeyDown={(event) => {
                    if (
                        isContextMenuShortcut(event) &&
                        !isFromTextField(event)
                    ) {
                        event.preventDefault();
                        openAtTrigger(event.currentTarget);
                    }
                }}
                onTouchStart={(event) => {
                    if (isFromTextField(event)) {
                        event.preventBaseUIHandler();
                    }
                }}
                onContextMenu={(event) => {
                    if (isFromTextField(event)) {
                        event.preventBaseUIHandler();
                        // Base UI's document listener blocks the browser's
                        // menu anywhere in the trigger.
                        event.stopPropagation();
                        return;
                    }
                    const isFromKeyboard =
                        event.nativeEvent.isTrusted &&
                        event.clientX === 0 &&
                        event.clientY === 0;
                    if (isFromKeyboard) {
                        event.preventBaseUIHandler();
                        event.preventDefault();
                        openAtTrigger(event.currentTarget);
                    }
                }}
            />
            <MenuPopup>{children}</MenuPopup>
        </ContextMenu.Root>
    );
};
